import React, { lazy, Suspense } from "react";

import { Routes, Route } from "react-router-dom";

import RouteFallback from "./components/RouteFallback";

/*
 * Every screen is fetched only when somebody goes to it.
 *
 * Loading them all up front meant one file of about 265KB compressed, so a
 * visitor reading the landing page downloaded the whole admin panel — the
 * project editor, the site-content editor, the message centre — before they
 * could see a single photograph. On a slow connection that is the difference
 * between a site that appears and one that is given up on.
 */
const Home = lazy(() => import("./pages/website/Home"));
const AboutUs = lazy(() => import("./pages/website/AboutUs"));
const Gallery = lazy(() => import("./pages/website/Gallery"));
const Offers = lazy(() => import("./pages/website/Offers"));
const SignIn = lazy(() => import("./pages/signIn/SignIn"));
const ProjectDetail = lazy(() => import("./pages/userPages/ProjectDetail"));
const UnitDetail = lazy(() => import("./pages/userPages/UnitDetail"));
const PrivateRoutes = lazy(() => import("./routes/PrivateRoutes"));

/*
 * The legal and support pages.
 *
 * Reachable without signing in, and without installing anything, because that
 * is what the app stores check for before a submission is reviewed: a privacy
 * policy, somewhere to get help, and a way to ask for an account to be deleted.
 *
 * `/privacy-policy` is here as well as `/privacy` because both spellings get
 * typed and linked, and a legal page that 404s is worse than one nobody reads.
 */
const Privacy = lazy(() => import("./pages/website/legal/Privacy"));
const Terms = lazy(() => import("./pages/website/legal/Terms"));
const Support = lazy(() => import("./pages/website/legal/Support"));
const DeleteAccount = lazy(() => import("./pages/website/legal/DeleteAccount"));

function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/projects/:projectId" element={<ProjectDetail />} />
        <Route path="/projects/units/:unitId" element={<UnitDetail />} />

        {/* legal and support — required before either app store will review */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-of-use" element={<Terms />} />
        {/*
          * No `/help` alias here: that path is the buyer's in-product help,
          * behind sign-in, and a public route of the same name declared first
          * would shadow it — the help page would quietly become the support
          * page for everybody signed in.
          */}
        <Route path="/support" element={<Support />} />
        <Route path="/delete-account" element={<DeleteAccount />} />

        {/* private routes routes */}
        <Route path="/*" element={<PrivateRoutes />} />
      </Routes>
    </Suspense>
  );
}

export default App;
